/**
 * The words a lookup row says, and where its sources may link. Pure, so they
 * are tested rather than read off a reply.
 */

import type { Lookup } from '../api';

/** What comes before the query: "Searching the web for", "Searched the docs
 *  for", "Could not search the web for". */
export function lookupLead(lookup: Lookup, running: boolean): string {
  const where = lookup.kind === 'web' ? 'the web' : 'the docs';
  if (running) return `Searching ${where} for`;
  if (lookup.failed) return `Could not search ${where} for`;
  return `Searched ${where} for`;
}

/** What follows the query once it is back: how many sources, or "Nothing
 *  found". Nothing while it runs, or when it failed. */
export function lookupResult(lookup: Lookup, running: boolean): string | null {
  if (running || lookup.failed) return null;
  const n = lookup.sources.length;
  if (n === 0) return 'Nothing found';
  return n === 1 ? '1 source' : `${n} sources`;
}

/** The query on one line. */
export const lookupQuery = (query: string): string => query.replace(/\s+/g, ' ').trim();

/** A source's address as a person reads it: "gov.uk", not
 *  "https://www.gov.uk/bank-holidays". */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** A source links only to an http or https page; anything else — a
 *  `javascript:` address from a hostile result — renders as text. */
export function sourceHref(url: string): string | null {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}
